/**
 * Success Stories Page - Olympic Fit Wellness
 * Member transformation stories and testimonials
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote, Play, Star } from 'lucide-react';

export default function SuccessStoriesPage() {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All Stories' },
    { id: 'weight-loss', name: 'Weight Loss' },
    { id: 'strength', name: 'Strength' },
    { id: 'athletic', name: 'Athletic Performance' },
    { id: 'wellness', name: 'Wellness Journey' }
  ];

  const stories = [
    {
      id: 'story_001',
      name: 'Sarah Mitchell',
      category: 'weight-loss',
      result: 'Lost 65 lbs',
      duration: '10 months',
      quote: 'Olympic Fit Wellness gave me the tools, support, and community I needed to completely transform my life. The combination of personal training, nutrition counseling, and group classes made all the difference.',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=600',
      beforeAfter: true
    },
    {
      id: 'story_002',
      name: 'Michael Thompson',
      category: 'athletic',
      result: 'Marathon Finisher',
      duration: '8 months',
      quote: 'From couch to marathon in 8 months. The trainers believed in me when I didn\'t believe in myself. Their sports-specific training program was exactly what I needed.',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600',
      beforeAfter: false
    },
    {
      id: 'story_003',
      name: 'Jennifer Lopez',
      category: 'wellness',
      result: 'Life Transformed',
      duration: '1 year',
      quote: 'More than just fitness - the mental wellness programs helped me manage chronic stress and find balance. I\'m sleeping better, eating better, and living better.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=600',
      beforeAfter: false
    },
    {
      id: 'story_004',
      name: 'David Chen',
      category: 'strength',
      result: 'Gained 25 lbs Muscle',
      duration: '14 months',
      quote: 'The strength training program and nutrition guidance helped me gain lean muscle mass I never thought possible. The trainers really know their stuff.',
      image: 'https://images.unsplash.com/photo-1568602471122-7832951cc4c5?w=600',
      beforeAfter: true
    },
    {
      id: 'story_005',
      name: 'Amanda Rodriguez',
      category: 'weight-loss',
      result: 'Lost 45 lbs',
      duration: '6 months',
      quote: 'After having my second child, I thought I\'d never get my body back. The pre/postnatal program was perfect for my journey back to fitness.',
      image: 'https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=600',
      beforeAfter: true
    },
    {
      id: 'story_006',
      name: 'Robert Kim',
      category: 'athletic',
      result: 'Triathlon Complete',
      duration: '12 months',
      quote: 'At 55, I completed my first triathlon. The age-appropriate training and recovery services made it possible to train at a high level safely.',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=600',
      beforeAfter: false
    }
  ];

  const filteredStories = activeCategory === 'all'
    ? stories
    : stories.filter(s => s.category === activeCategory);

  const featuredVideo = {
    title: 'John\'s 100lb Weight Loss Journey',
    thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800'
  };

  return (
    <div className="bg-white" data-testid="success-stories-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">MEMBER SUCCESS</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
            Success Stories
          </h1>
          <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto tracking-wide">
            Real members, real results. Discover how Olympic Fit Wellness has transformed lives.
          </p>
        </div>
      </section>

      {/* Featured Video */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="relative aspect-video overflow-hidden mb-6 group cursor-pointer">
              <img 
                src={featuredVideo.thumbnail}
                alt={featuredVideo.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="w-8 h-8 text-neutral-900 ml-1" />
                </div>
              </div>
            </div>
            <h2 className="text-2xl font-heading font-normal text-neutral-900 mb-2 tracking-wide text-center">
              {featuredVideo.title}
            </h2>
            <p className="text-neutral-500 text-center">Watch John's incredible transformation</p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-20 z-30 bg-white border-b border-neutral-200 py-4">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`px-6 py-3 text-sm font-heading tracking-[0.06em] whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-black text-white'
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
              >
                {cat.name.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stories Grid */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStories.map((story) => (
              <div 
                key={story.id} 
                className="group"
                data-testid={`story-${story.id}`}
              >
                <div className="aspect-[3/4] overflow-hidden mb-6 relative">
                  <img 
                    src={story.image}
                    alt={story.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {story.beforeAfter && (
                    <div className="absolute top-4 left-4 bg-black text-white px-3 py-1 text-xs font-heading tracking-wider">
                      BEFORE & AFTER
                    </div>
                  )}
                </div>
                
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-neutral-900">{story.result}</span>
                  <span className="text-neutral-300">•</span>
                  <span className="text-sm text-neutral-500">{story.duration}</span>
                </div>
                
                <h3 className="text-xl font-heading font-normal text-neutral-900 mb-3 tracking-wide">
                  {story.name}
                </h3>
                
                <p className="text-neutral-600 text-sm leading-relaxed mb-4 line-clamp-3">
                  "{story.quote}"
                </p>
                
                <Link
                  to={`/success-stories/${story.id}`}
                  className="inline-flex items-center gap-2 text-sm font-heading tracking-[0.06em] text-neutral-900 hover:text-neutral-600 transition-colors"
                >
                  READ FULL STORY <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-24 bg-neutral-900">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">BY THE NUMBERS</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-white mb-6 tracking-[0.04em] uppercase">
              Collective Member Success
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '500K+', label: 'Pounds Lost' },
              { value: '10K+', label: 'Transformations' },
              { value: '98%', label: 'Goal Achievement' },
              { value: '4.9/5', label: 'Member Rating' }
            ].map((stat, idx) => (
              <div key={idx}>
                <div className="text-4xl md:text-5xl font-heading font-normal text-white mb-2">{stat.value}</div>
                <div className="text-sm text-neutral-500 tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-heading tracking-[0.15em] text-neutral-500 mb-4 block">REVIEWS</span>
            <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
              What Members Say
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { name: 'Alex P.', rating: 5, text: 'Best gym experience I\'ve ever had. The staff is amazing and the facilities are top-notch.' },
              { name: 'Maria G.', rating: 5, text: 'The nutrition program changed my relationship with food completely. Worth every penny.' },
              { name: 'Tom B.', rating: 5, text: 'I\'ve been to many gyms, but OFW is in a league of its own. True comprehensive wellness.' }
            ].map((review, idx) => (
              <div key={idx} className="bg-neutral-50 p-8">
                <div className="flex gap-1 mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-neutral-600 mb-4 italic">"{review.text}"</p>
                <p className="font-heading font-medium text-neutral-900">{review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="container mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-normal text-neutral-900 mb-6 tracking-[0.04em] uppercase">
            Ready to Write Your Success Story?
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-10 tracking-wide">
            Join the thousands of members who have transformed their lives with Olympic Fit Wellness.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/membership"
              className="inline-flex items-center gap-2 bg-black text-white font-heading px-10 py-4 text-sm tracking-[0.08em] hover:bg-neutral-800 transition-colors"
            >
              START YOUR JOURNEY <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-neutral-300 text-neutral-700 font-heading px-10 py-4 text-sm tracking-[0.08em] hover:border-black transition-colors"
            >
              SCHEDULE A TOUR
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
